// src/pages/builder/ShopBulkAdd.js
//
// BULK ADD SHOP ITEMS (Layout Builder → Rewards & avatar settings → Shop
// items → ⇪ Bulk add). Two ways in, one review table out:
//
//   1. Pictures  — pick many image files at once. Each becomes a row named
//                  after its file ("holly-leaf-pin.png" → "Holly Leaf Pin").
//                  PNGs with a see-through background go up as they are; any
//                  other picture has its white background cut out.
//   2. AI list   — one item per line:   Name | kind | price | what it looks like
//                  e.g.  Reindeer Antlers | hat | 400 | brown antlers with a red bow
//                  Each line is drawn by AI, one after another.
//
// Set the kind, price and season for every row at once (or per row), check
// the pictures, then "Add all to the shop".

import React, { useState } from "react";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/firebaseConfig";
import { ItemArt, SLOT_LABELS } from "../../components/avatar/wearableArt";
import { KINDS, KIND_WORD, SeasonFields, cleanSeason, cutOutWhite, slugify, uploadPng } from "./ShopItemsPanel";

const titleFromFile = (f) =>
  f
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const guessKind = (name) => {
  const n = name.toLowerCase();
  if (/necklace|lights|collar|scarf|chain|lei/.test(n)) return "necklace";
  if (/pin|badge/.test(n)) return "pin";
  if (/hat|crown|antler|cap|helmet|headband|beanie|ears/.test(n)) return "hat";
  if (/wing/.test(n)) return "wings";
  if (/ring|frame/.test(n)) return "frame";
  if (/glasses|shades/.test(n)) return "glasses";
  if (/puppy|kitten|dog|cat|pet|reindeer|snowman|dragon|bunny|turkey|owl/.test(n)) return "pet";
  return "hat";
};

const ShopBulkAdd = ({ existingIds, onDone }) => {
  const [rows, setRows] = useState([]); // { key, name, kind, price, idea, pic: {url,path}|null, status }
  const [defaults, setDefaults] = useState({ kind: "", price: "", season: { season: "", availableFrom: "", availableUntil: "" } });
  const [list, setList] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  const patch = (key, p) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy("upload");
    setMsg(null);
    const added = files.map((f, i) => {
      const name = titleFromFile(f.name);
      return { key: `${Date.now()}-${i}`, name, kind: defaults.kind || guessKind(name), price: defaults.price || "200", idea: "", pic: null, status: "uploading…", file: f };
    });
    setRows((rs) => [...rs, ...added]);
    for (const r of added) {
      try {
        let blob = r.file;
        if (!/png/i.test(r.file.type)) {
          const local = URL.createObjectURL(r.file);
          // eslint-disable-next-line no-await-in-loop
          blob = (await cutOutWhite(local)) || r.file;
          URL.revokeObjectURL(local);
        }
        // eslint-disable-next-line no-await-in-loop
        const up = await uploadPng(blob, r.name);
        patch(r.key, { pic: up, status: "ready", file: null });
      } catch (err) {
        patch(r.key, { status: `upload failed: ${err?.message || err}` });
      }
    }
    setBusy("");
  };

  const drawList = async () => {
    const lines = list
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return;
    const added = lines.map((l, i) => {
      const [name = "", kind = "", price = "", idea = ""] = l.split("|").map((x) => x.trim());
      const k = KINDS.includes(kind.toLowerCase()) ? kind.toLowerCase() : defaults.kind || guessKind(name);
      return { key: `${Date.now()}-ai-${i}`, name, kind: k, price: price || defaults.price || "200", idea: idea || name, pic: null, status: "waiting…" };
    });
    setRows((rs) => [...rs, ...added]);
    setList("");
    setBusy("ai");
    setMsg(null);
    for (const r of added) {
      patch(r.key, { status: "drawing… (up to a minute)" });
      try {
        const prompt = [
          `A single ${KIND_WORD[r.kind] || r.kind} for a children's game avatar: ${r.idea}.`,
          "Cartoon sticker style: bold dark outlines, flat bright colours, one soft highlight, friendly and cute.",
          "Show ONLY the item — no person, head or body wearing it, no shadow, no text.",
          "Centred, filling most of the square, on a plain pure white background.",
        ].join("\n");
        // eslint-disable-next-line no-await-in-loop
        const res = await httpsCallable(functions, "generateImage", { timeout: 170000 })({ prompt, style: "none", model: "standard", aspectRatio: "1:1", name: `shop-${slugify(r.name)}`, refs: [] });
        const url = res?.data?.url;
        if (!url) throw new Error("no picture came back");
        // eslint-disable-next-line no-await-in-loop
        const png = await cutOutWhite(url);
        // eslint-disable-next-line no-await-in-loop
        const pic = png ? await uploadPng(png, r.name) : { url, path: res.data.path || "" };
        patch(r.key, { pic, status: "ready" });
      } catch (err) {
        patch(r.key, { status: `AI failed: ${err?.message || err}` });
      }
    }
    setBusy("");
  };

  const applyDefaults = () =>
    setRows((rs) =>
      rs.map((r) => ({
        ...r,
        ...(defaults.kind ? { kind: defaults.kind } : {}),
        ...(defaults.price !== "" ? { price: defaults.price } : {}),
        season: defaults.season.season ? { ...defaults.season } : r.season,
      }))
    );

  const ready = rows.filter((r) => r.pic && r.name.trim());

  const addAll = async () => {
    setBusy("save");
    setMsg(null);
    const used = new Set(existingIds);
    let n = 0;
    try {
      for (const r of ready) {
        const base = `${r.kind}-${slugify(r.name)}`;
        let id = base;
        for (let k = 2; used.has(id); k += 1) id = `${base}-${k}`;
        used.add(id);
        // eslint-disable-next-line no-await-in-loop
        await setDoc(doc(db, "shopItems", id), {
          name: r.name.trim(),
          kind: r.kind,
          price: Math.max(0, Math.floor(Number(r.price) || 0)),
          imageUrl: r.pic.url,
          imagePath: r.pic.path || "",
          prompt: r.idea || "",
          ...cleanSeason(r.season || defaults.season),
          active: true,
          sort: 100,
          createdAt: serverTimestamp(),
        });
        n += 1;
      }
      setRows((rs) => rs.filter((r) => !(r.pic && r.name.trim())));
      setMsg({ tone: "ok", text: `Added ${n} item${n === 1 ? "" : "s"} to the shop.` });
      if (n && onDone && rows.length === n) onDone();
    } catch (err) {
      setMsg({ tone: "bad", text: `Stopped after ${n}: ${err?.message || err}` });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="shp-bulk">
      <h3>⇪ Bulk add</h3>
      <div className="shp-bulk-ways">
        <div>
          <b>1. Upload pictures</b>
          <p className="shp-tiny">Pick many at once. Name the files well — "holly-leaf-pin.png" becomes "Holly Leaf Pin". See-through PNGs look best.</p>
          <label className={`lb-btn lb-btn-sm shp-upload ${busy ? "is-disabled" : ""}`}>
            {busy === "upload" ? "Uploading…" : "⬆ Choose pictures"}
            <input type="file" multiple accept="image/png,image/jpeg,image/webp" onChange={onFiles} disabled={Boolean(busy)} />
          </label>
        </div>
        <div>
          <b>2. Make them with AI</b>
          <p className="shp-tiny">One per line: <code>Name | kind | price | what it looks like</code></p>
          <textarea
            rows={4}
            value={list}
            onChange={(e) => setList(e.target.value)}
            placeholder={"Reindeer Antlers | hat | 400 | brown antlers with a red bow and two jingle bells\nChristmas Lights | necklace | 450 | a string of bright coloured holiday light bulbs\nHolly Leaf Pin | pin | 150 | two green holly leaves with three red berries"}
          />
          <button type="button" className="lb-btn lb-btn-sm" disabled={Boolean(busy) || !list.trim()} onClick={drawList}>
            {busy === "ai" ? "Drawing…" : "✨ Draw them all"}
          </button>
        </div>
      </div>

      <div className="shp-bulk-defaults">
        <b>Set for every row:</b>
        <select value={defaults.kind} onChange={(e) => setDefaults({ ...defaults, kind: e.target.value })} aria-label="Kind for all">
          <option value="">kind…</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {SLOT_LABELS[k]}
            </option>
          ))}
        </select>
        <input type="number" min="0" placeholder="price" value={defaults.price} onChange={(e) => setDefaults({ ...defaults, price: e.target.value })} style={{ width: 80 }} />
        <SeasonFields value={defaults.season} onChange={(v) => setDefaults({ ...defaults, season: v })} />
        <button type="button" className="lb-btn lb-btn-sm lb-btn-soft" onClick={applyDefaults} disabled={!rows.length}>
          Apply to all rows
        </button>
      </div>

      {rows.length > 0 && (
        <div className="shp-bulk-rows">
          {rows.map((r) => (
            <div key={r.key} className="shp-bulk-row">
              <span className={`shp-art shp-art-${r.kind}`}>{r.pic ? <ItemArt item={{ kind: r.kind, imageUrl: r.pic.url }} /> : <span className="shp-tiny">…</span>}</span>
              <input value={r.name} onChange={(e) => patch(r.key, { name: e.target.value })} aria-label="Name" />
              <select value={r.kind} onChange={(e) => patch(r.key, { kind: e.target.value })} aria-label="Kind">
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {SLOT_LABELS[k]}
                  </option>
                ))}
              </select>
              <label className="shp-price">
                🪙
                <input type="number" min="0" value={r.price} onChange={(e) => patch(r.key, { price: e.target.value })} aria-label="Price" />
              </label>
              <SeasonFields value={r.season || { season: "" }} onChange={(v) => patch(r.key, { season: v })} />
              <span className="shp-tiny">{r.status}</span>
              <button type="button" className="shp-x" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} aria-label="Remove row">
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="shp-bulk-foot">
        <button type="button" className="lb-btn lb-btn-save" disabled={Boolean(busy) || !ready.length} onClick={addAll}>
          {busy === "save" ? "Adding…" : `Add ${ready.length} to the shop`}
        </button>
        {msg && <span className={`shp-msg is-${msg.tone}`}>{msg.text}</span>}
      </div>
    </div>
  );
};

export default ShopBulkAdd;
